import type { ViewingTokenResponse } from "@punchup/video/client";
import { SpecialRow } from "@/data/specials";

export interface ResolveSpecialVideoSrcInput {
  special: Pick<SpecialRow, "id" | "video_id" | "preview_video_id">;
  /** Server-computed access flag (can be momentarily stale right after a subscribe). */
  userCanAccessSpecial: boolean;
  /** Gate: when true, the client viewing token is the source of truth for access. */
  resolveAccessClientSide: boolean;
  getViewingToken: (specialId: string) => Promise<ViewingTokenResponse | null>;
  getPreviewUrl: (previewVideoId: string) => Promise<string | null>;
}

export interface SpecialVideoSource {
  videoSrc: string | null;
  isPreview: boolean;
  /** True only on the trusted-server path when the token request itself failed. */
  tokenError: boolean;
  /** True only on the client-resolution path when the token denied full access, so the caller can bounce to /checkout. */
  resolvedNoAccess: boolean;
}

/**
 * Decides which video source the special-page player should load.
 *
 * When `resolveAccessClientSide` is on, the server-rendered `userCanAccessSpecial`
 * is ignored and the (read-your-writes-consistent) client viewing token decides:
 * `hasAccess` → the full video, otherwise the preview plus `resolvedNoAccess`.
 * This lets a just-subscribed user play the content even when the server flag
 * is momentarily stale.
 *
 * When the gate is off, the original behaviour is preserved exactly: trust the
 * server flag, and surface a token error so the caller can toast.
 */
export async function resolveSpecialVideoSrc({
  special,
  userCanAccessSpecial,
  resolveAccessClientSide,
  getViewingToken,
  getPreviewUrl,
}: ResolveSpecialVideoSrcInput): Promise<SpecialVideoSource> {
  const loadPreview = async (
    resolvedNoAccess = false
  ): Promise<SpecialVideoSource> => {
    const videoSrc = special.preview_video_id
      ? await getPreviewUrl(special.preview_video_id)
      : null;
    return { videoSrc, isPreview: true, tokenError: false, resolvedNoAccess };
  };

  if (resolveAccessClientSide) {
    if (special.video_id) {
      const tokenRes = await getViewingToken(special.id);
      if (tokenRes?.hasAccess) {
        return {
          videoSrc: tokenRes.token,
          isPreview: false,
          tokenError: false,
          resolvedNoAccess: false,
        };
      }
    }
    return loadPreview(true);
  }

  if (special.video_id && userCanAccessSpecial) {
    const tokenRes = await getViewingToken(special.id);
    return {
      videoSrc: tokenRes?.hasAccess ? tokenRes.token : null,
      isPreview: false,
      tokenError: tokenRes === null,
      resolvedNoAccess: false,
    };
  }

  if (special.preview_video_id && !userCanAccessSpecial) {
    return loadPreview();
  }

  return {
    videoSrc: null,
    isPreview: false,
    tokenError: false,
    resolvedNoAccess: false,
  };
}
